import { useEffect, useRef, useState } from 'react';

// Split text into per-letter spans for wind animation
function WindText({ children }: { children: string }) {
  const chars = Array.from(children);
  return (
    <span className="wind-text">
      {chars.map((ch, i) => (
        <span
          key={`${ch}-${i}`}
          className="wind-letter"
          style={{ ['--wind-delay' as string]: `${(i % 7) * 0.08}s` }}
        >
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      ))}
    </span>
  );
}

const HERO_VIDEO =
  'https://public.readdy.ai/ai/video_res/019ed4d6-319c-7b51-b255-a31dc5d4540b.mp4';

export default function Home() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [windy, setWindy] = useState(false);
  const windTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let targetTime = 0;
    let rafId = 0;
    let seeking = false;
    let pendingTime = -1;

    // sensitivity: pixels of scroll per second of video (smaller = faster)
    const PIXELS_PER_SECOND = 400;

    const applySeek = () => {
      if (seeking) {
        pendingTime = targetTime;
        return;
      }
      if (Math.abs(video.currentTime - targetTime) < 0.001) return;
      seeking = true;
      pendingTime = -1;
      video.currentTime = targetTime;
    };

    const onSeeked = () => {
      seeking = false;
      if (pendingTime >= 0 && Math.abs(pendingTime - video.currentTime) > 0.001) {
        const next = pendingTime;
        pendingTime = -1;
        seeking = true;
        video.currentTime = next;
      }
    };

    const advance = (deltaPx: number) => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (duration <= 0) return;
      const seconds = deltaPx / PIXELS_PER_SECOND;
      targetTime = Math.min(Math.max(targetTime + seconds, 0), duration - 0.05);
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          applySeek();
        });
      }
    };

    const triggerWind = () => {
      setWindy(true);
      if (windTimerRef.current) window.clearTimeout(windTimerRef.current);
      windTimerRef.current = window.setTimeout(() => {
        setWindy(false);
      }, 120);
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      advance(e.deltaY);
      triggerWind();
    };

    let lastTouchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const y = e.touches[0]?.clientY ?? 0;
      const delta = lastTouchY - y;
      lastTouchY = y;
      advance(delta * 3);
      triggerWind();
    };

    const onLoaded = () => {
      video.pause();
      video.currentTime = 0;
      targetTime = 0;
    };

    video.pause();
    if (video.readyState >= 1) {
      onLoaded();
    } else {
      video.addEventListener('loadedmetadata', onLoaded);
    }
    video.addEventListener('seeked', onSeeked);

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (windTimerRef.current) window.clearTimeout(windTimerRef.current);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('seeked', onSeeked);
    };
  }, []);

  return (
    <div className="h-screen bg-cream font-body text-[#2F3B2E] overflow-hidden">
      {/* Hero Section – single full screen, scroll-scrubbed video */}
      <section className="relative w-full h-screen bg-cream overflow-hidden">
        {/* Background Video (scroll-controlled, no autoplay) */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover z-0"
          style={{
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
          }}
          src={HERO_VIDEO}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
        />

        {/* Top Navigation */}
        <nav className="relative z-20 w-full px-6 md:px-12 py-6 flex items-center justify-between">
          <a href="#" className="flex items-center gap-2 cursor-pointer group">
            <span className="w-10 h-10 rounded-full bg-blossom-500 flex items-center justify-center text-white shadow-[0_4px_0_#F25C95] group-hover:translate-y-[2px] group-hover:shadow-[0_2px_0_#F25C95] transition-all">
              <i className="ri-leaf-line text-lg"></i>
            </span>
            <span className="text-2xl text-forest-700 font-display tracking-wide">
              Whimsy Woods
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8 font-ui font-semibold text-[15px] text-forest-700">
            <a href="#stories" className="hover:text-blossom-600 transition-colors whitespace-nowrap cursor-pointer">
              Stories
            </a>
            <a href="#characters" className="hover:text-blossom-600 transition-colors whitespace-nowrap cursor-pointer">
              Characters
            </a>
            <a href="#world" className="hover:text-blossom-600 transition-colors whitespace-nowrap cursor-pointer">
              Our World
            </a>
            <a href="#shop" className="hover:text-blossom-600 transition-colors whitespace-nowrap cursor-pointer">
              Shop
            </a>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="#login"
              className="hidden md:inline-flex items-center font-ui font-semibold text-forest-700 hover:text-blossom-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Sign in
            </a>
            <a
              href="#join"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest-500 text-white font-ui font-semibold text-sm shadow-[0_4px_0_#2F8546] hover:translate-y-[2px] hover:shadow-[0_2px_0_#2F8546] transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-sparkling-2-fill"></i>
              Begin Adventure
            </a>
          </div>
        </nav>

        {/* Hero content – sits in the empty right space of the illustration */}
        <div className="relative z-10 w-full px-6 md:px-12 pt-2 md:pt-6">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 items-start gap-10">
            <div className="hidden lg:block lg:col-span-5" />

            <div className="lg:col-span-7 lg:pl-6 lg:translate-x-[50px] mt-[150px]">
              <h1
                className={`font-display text-[54px] md:text-[74px] xl:text-[88px] leading-[0.95] text-forest-700 ${windy ? 'wind-active' : ''}`}
              >
                <span className="block"><WindText>Step into a</WindText></span>
                <span className="block text-blossom-500"><WindText>storybook</WindText></span>
                <span className="block"><WindText>where leaves</WindText></span>
                <span className="block">
                  <WindText>whisper </WindText>
                  <span className="relative inline-block text-sky-500">
                    <WindText>hello</WindText>
                    <span className="absolute -bottom-1 left-0 right-0 h-2 rounded-full bg-sunshine-200" />
                  </span>
                  <WindText>.</WindText>
                </span>
              </h1>

              <p className="mt-5 text-lg md:text-xl text-[#5b6a59] max-w-xl leading-snug font-medium">
                A cozy little universe of handcrafted illustrations, bedtime tales
                and friendly creatures — made for big imaginations 
              </p>

              {/* CTA buttons */}
              <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href="#explore"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-blossom-500 text-white font-ui font-bold text-base shadow-[0_6px_0_#F25C95] hover:translate-y-[3px] hover:shadow-[0_3px_0_#F25C95] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-compass-3-line text-lg"></i>
                  Explore the Woods
                </a>
                <a
                  href="#watch"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-forest-700 font-ui font-bold text-base border-2 border-forest-500 hover:bg-forest-500 hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-forest-500 text-white">
                    <i className="ri-play-fill text-sm ml-0.5"></i>
                  </span>
                  Watch the trailer
                </a>
              </div>

            </div>
          </div>
        </div>

        {/* Floating decorative blobs */}
        <span className="hidden md:block absolute top-32 right-24 w-4 h-4 rounded-full bg-blossom-300 animate-floaty" />
        <span className="hidden md:block absolute top-56 right-[28%] w-3 h-3 rounded-full bg-sky-300 animate-floaty-slow" />
        <span className="hidden md:block absolute bottom-44 left-[42%] w-5 h-5 rounded-full bg-sunshine-200 animate-floaty" />
        <span className="hidden md:block absolute top-44 left-[55%] w-2.5 h-2.5 rounded-full bg-forest-500 animate-floaty-slow" />
      </section>
    </div>
  );
}